import React, { useContext, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import queryString from "query-string";
import * as XLSX from "xlsx-js-style";

import lbl from "../../data/Labels.json";
import Img from "../common/Img";
import db from "../../services/query";
import MainButton from "../common/MainButton";
import MainPrintLink from "../common/MainPrint";
import DailySalaryList from "./DailySalaryList";
import {
  formatDateFa,
  getDailySalaryReportLabels,
} from "./dailySalaryReportLabels";
import { AuthContext } from "../../contexts/authContext";

function formatMoney(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  const formatted = n.toLocaleString("en-US", {
    minimumFractionDigits: n % 1 ? 2 : 0,
    maximumFractionDigits: 2,
  });
  return `${formatted} $`;
}

const DailySalaryProfile = (props) => {
  const { tid } = props.match.params;
  const { teacherName } = queryString.parse(props.location.search);
  const { accountProfile } = useContext(AuthContext);
  const isTeacherViewer = Number(accountProfile?.position_id) === 1;

  const contractsHref = `/mis/teachers/${tid}/profile/contracts?teacherName=${encodeURIComponent(
    teacherName || ""
  )}`;

  const [data, setData] = useState(null);
  const [entryYearId, setEntryYearId] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [loading, setLoading] = useState(false);

  const fetchDaily = async (year, fromDate, toDate, all = false) => {
    const params = [];
    if (all) params.push("all=1");
    else if (year) params.push(`entry_year_id=${year}`);
    if (fromDate) params.push(`from=${fromDate}`);
    if (toDate) params.push(`to=${toDate}`);
    const qs = params.length ? `?${params.join("&")}` : "";
    const res = await db.getAll(`teachers/${tid}/salary/daily${qs}`);
    return res?.data || null;
  };

  const requestData = async (
    year = entryYearId,
    fromDate = from,
    toDate = to
  ) => {
    setLoading(true);
    try {
      const all = year === "";
      setData(await fetchDaily(year, fromDate, toDate, all));
    } catch (_e) {
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    setFrom("");
    setTo("");
    setData(null);

    (async () => {
      setLoading(true);
      try {
        // One request: API defaults to current semester when no year/all is sent.
        const body = await fetchDaily("", "", "", false);
        if (cancelled) return;
        const applied =
          body?.summary?.applied_entry_year_id ||
          body?.summary?.current_entry_year_id ||
          "";
        setEntryYearId(applied ? String(applied) : "");
        setData(body);
      } catch (_e) {
        if (!cancelled) setData(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [tid]);

  const summary = data?.summary;
  const days = data?.data || [];
  const entryYears = summary?.entry_years || [];
  const totals = summary?.totals || {};
  const teacher = summary?.teacher || {};
  const selectedYear = entryYears.find(
    (ey) => String(ey.entry_year_id) === String(entryYearId)
  );
  const rate = selectedYear
    ? Number(selectedYear.salary_per_hour || 0)
    : entryYears.length === 1
    ? Number(entryYears[0].salary_per_hour || 0)
    : null;
  const hasRate = rate != null && rate > 0;
  const paidSessions = Number(totals.paid_session_count || 0);
  const missingRate = !hasRate && paidSessions > 0;
  const semesterLabel = selectedYear?.entry_year || "همه سمسترها";
  const teacherLabel =
    teacher?.teacher_name || teacherName || `teacher_${tid}`;
  const periodEarn = Number(totals.day_earn_gross || 0);
  const benefitTotal = Number(totals.total_benefit || 0);
  const grandTotal = Number(
    totals.with_benefits != null
      ? totals.with_benefits
      : periodEarn + benefitTotal
  );
  const reportLabels = getDailySalaryReportLabels({
    yearLabel: semesterLabel,
    from,
    to,
  });

  const stats = [
    {
      label: "حاضر",
      hint: "جلساتی که خود استاد حاضر بوده",
      value: totals.present_count || 0,
      tone: "ok",
    },
    {
      label: "جایگزین",
      hint: "جلساتی که به‌جای استاد دیگر تدریس کرده",
      value: totals.substitute_count || 0,
      tone: "info",
    },
    {
      label: "غیر حاضر",
      hint: "جلساتی غیر حاضر",
      value: totals.absent_count || 0,
      tone: "warn",
    },
    {
      label: "کردت قابل پرداخت",
      hint: "تعداد جلسات حاضر و جایگزین",
      value: paidSessions,
      tone: "info",
    },
    {
      label: "قابل پرداخت",
      hint: "معاش جلسات + امتیازات",
      value: formatMoney(grandTotal),
      tone: "pay",
    },
  ];

  const onSemesterChange = (value) => {
    setEntryYearId(value);
    requestData(value, from, to);
  };

  const printQuery = [
    entryYearId ? `entry_year_id=${entryYearId}` : "all=1",
    from ? `from=${from}` : "",
    to ? `to=${to}` : "",
    teacherName ? `teacherName=${encodeURIComponent(teacherName)}` : "",
  ]
    .filter(Boolean)
    .join("&");

  const handleExportExcel = () => {
    if (!days.length) return;

    const headers = [
      "تاریخ",
      "تاریخ شمسی",
      "سمستر",
      "مضامین همان روز",
      "حاضر",
      "جایگزین",
      "غایب",
      "کردت قابل پرداخت",
      "معاش فی ساعت",
      "معاش روز",
      "قابل پرداخت",
    ];

    const rows = days.map((row) => [
      String(row.attendance_date || "").slice(0, 10),
      formatDateFa(row.attendance_date),
      row.entry_year || "",
      row.subjects || "",
      Number(row.present_count || 0),
      Number(row.substitute_count || 0),
      Number(row.absent_count || 0),
      Number(row.paid_session_count || 0),
      Number(row.salary_per_hour || 0),
      Number(row.day_earn_gross || 0),
      Number(row.day_earn_gross || 0),
    ]);

    rows.push([
      "امتیازات",
      "",
      "",
      "",
      "",
      "",
      "",
      "",
      "",
      "",
      Number(totals.total_benefit || 0),
    ]);

    rows.push([
      "مجموع",
      "",
      "",
      "",
      Number(totals.present_count || 0),
      Number(totals.substitute_count || 0),
      Number(totals.absent_count || 0),
      Number(totals.paid_session_count || 0),
      "",
      Number(totals.day_earn_gross || 0),
      Number(
        totals.with_benefits != null
          ? totals.with_benefits
          : Number(totals.day_earn_gross || 0) +
              Number(totals.total_benefit || 0)
      ),
    ]);

    const sheetData = [
      [`${reportLabels.excelTitle} — ${teacherLabel}`],
      [`سمستر: ${semesterLabel}`],
      headers,
      ...rows,
    ];

    const worksheet = XLSX.utils.aoa_to_sheet(sheetData);
    worksheet["!cols"] = [
      { wch: 12 },
      { wch: 14 },
      { wch: 16 },
      { wch: 48 },
      { wch: 8 },
      { wch: 8 },
      { wch: 8 },
      { wch: 12 },
      { wch: 12 },
      { wch: 12 },
      { wch: 12 },
    ];

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Daily Salary");
    const safeName = String(teacherLabel)
      .replace(/[\\/:*?"<>|]/g, "-")
      .replace(/\s+/g, "_")
      .slice(0, 60);
    XLSX.writeFile(
      workbook,
      `teacher_${tid}_daily_salary_${safeName || "export"}.xlsx`,
      { bookType: "xlsx", compression: true }
    );
  };

  return (
    <div className="daily-salary-page">
      <div className="finance-profile-area daily-salary-hero">
        <div className="std-profile">
          <div className="wrapper">
            <Img src={teacher?.teacher_photo} border={true} />
          </div>
        </div>
        <div className="overall-summary">
          <div className="wrapper">
            <p>مجموع (معاش جلسات + امتیازات)</p>
            <h1 className={missingRate ? "is-muted" : ""}>
              {formatMoney(grandTotal)}
            </h1>
            <span className="daily-salary-hero-note">
              سمستر: <b>{semesterLabel}</b>
              {" · "}
              معاش فی ساعت:{" "}
              <b className={hasRate ? "" : "is-missing"}>
                {hasRate ? formatMoney(rate) : "ثبت نشده"}
              </b>
              {" · "}
              جلسات: <b>{formatMoney(periodEarn)}</b>
              {" · "}
              امتیازات: <b>{formatMoney(benefitTotal)}</b>
            </span>
          </div>
        </div>
        <div className="controls">
          <MainPrintLink
            path={`teacher-daily-salary/${tid}${printQuery ? `?${printQuery}` : ""}`}
            title={lbl.Print}
          />
          <MainButton
            title={lbl.Excel}
            type="excel"
            handleClick={handleExportExcel}
          />
        </div>
      </div>

      {!isTeacherViewer && missingRate ? (
        <div className="daily-salary-alert" role="status">
          <div>
            <strong>نرخ معاش برای این سمستر ثبت نشده است</strong>
            <p>
              {paidSessions} جلسه قابل پرداخت دارید، اما چون قرارداد / معاش فی
              ساعت برای «{semesterLabel}» صفر است، معاش امروز{" "}
              <b>0 $</b> نشان داده می‌شود. برای محاسبه صحیح، نرخ را در تب
              قراردادها ثبت کنید.
            </p>
          </div>
          <Link className="daily-salary-alert-link" to={contractsHref}>
            رفتن به قرارداد ها
          </Link>
        </div>
      ) : null}

      <div className="daily-salary-toolbar">
        <div className="daily-salary-filters">
          <label>
            <span>سمستر</span>
            <select
              value={entryYearId}
              onChange={(e) => onSemesterChange(e.target.value)}
            >
              <option value="">همه سمسترها</option>
              {entryYears.map((ey) => (
                <option key={ey.entry_year_id} value={ey.entry_year_id}>
                  {ey.entry_year}
                  {Number(ey.salary_per_hour)
                    ? ` — نرخ ${formatMoney(ey.salary_per_hour)}`
                    : " — نرخ ثبت نشده"}
                </option>
              ))}
            </select>
          </label>
          <label>
            <span>از تاریخ</span>
            <input
              type="date"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
            />
          </label>
          <label>
            <span>تا تاریخ</span>
            <input
              type="date"
              value={to}
              onChange={(e) => setTo(e.target.value)}
            />
          </label>
          <button
            type="button"
            className="daily-salary-filter-btn"
            onClick={() => requestData()}
            disabled={loading}
          >
            {loading ? "..." : "فیلتر کردن"}
          </button>
        </div>
      </div>

      <div className="daily-salary-stats">
        {stats.map((s) => (
          <div key={s.label} className={`daily-salary-stat tone-${s.tone}`}>
            <b>{s.value}</b>
            <span>{s.label}</span>
            {s.hint ? <small>{s.hint}</small> : null}
          </div>
        ))}
      </div>

      <div className="finance-report-area daily-salary-report">
        <div className="report-container" style={{ width: "100%" }}>
          <div className="report-header">
            <div className="title">
              <p>{reportLabels.tableTitle}</p>
            </div>
            <div className="controls">
              <span className="daily-salary-day-count">
                {days.length
                  ? `${days.length} روز ثبت‌شده`
                  : loading
                  ? "در حال بارگذاری..."
                  : "بدون ردیف"}
              </span>
            </div>
          </div>

          <div className="report-table-area">
            {days.length ? (
              <DailySalaryList
                data={days}
                totals={totals}
                missingRate={missingRate}
                formatDateFa={formatDateFa}
                formatMoney={formatMoney}
              />
            ) : (
              <div className="daily-salary-empty">
                <div className="daily-salary-empty-icon" aria-hidden="true">
                  $
                </div>
                <h3>
                  {loading
                    ? "در حال بارگذاری..."
                    : "برای این تاریخ معاشی ثبت نشده است"}
                </h3>
                {!loading ? (
                  <p>
                    سمستر دیگری انتخاب کنید، یا تاریخ را تغییر دهید.
                  </p>
                ) : null}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DailySalaryProfile;
